import React, { useState } from 'react';
import {
  AlertTriangle,
  HelpCircle,
  MessageCircleQuestion,
  ThumbsUp,
  ThumbsDown,
} from 'lucide-react';
import {
  ManualAnswer,
  ManualStep,
  Citation,
  FeedbackRating,
  submitFeedback,
} from '../../services/umaService';
import { useManualAsset } from '../../hooks/useObjectUrl';

// Defense-in-depth: the query pipeline already strips markdown image syntax
// from summary/gaps/step.instruction server-side (T-117-18 — tampering via
// rendered content). Strip it again client-side so a future model response
// slipping one through never smuggles an <img> into the DOM.
const MD_IMAGE_RE = /!\[[^\]]*\]\([^)]*\)/g;
const stripMarkdownImages = (text: string): string => text.replace(MD_IMAGE_RE, '').trim();

/**
 * Derive a human "Figure 5.4" caption from an asset URL/filename like
 * `/api/v1/uma/assets/5.4.png`. Falls back to nothing recognisable → generic.
 */
const figureLabel = (url: string): string => {
  const file = (url.split('/').pop() || '').replace(/\.(png|jpe?g|gif|webp)$/i, '');
  return /^\d/.test(file) ? `Figure ${file}` : 'Screenshot';
};

/**
 * A single manual screenshot, blob-fetched through the JWT-carrying apiClient
 * (T-117-17) via useManualAsset — never a plain `<img src>`. Rendered inline
 * as a captioned <figure>; clicking it opens a full-size lightbox overlay so
 * the reader can actually see the detail. Loading/error degrade to a labelled
 * placeholder rather than a broken image.
 */
const ManualScreenshot: React.FC<{ url: string }> = ({ url }) => {
  const { src, loading, error } = useManualAsset(url);
  const [zoomed, setZoomed] = useState(false);
  const caption = figureLabel(url);

  if (loading) {
    return <div className="uma-screenshot-placeholder">Loading {caption}…</div>;
  }
  if (error || !src) {
    return (
      <div className="uma-screenshot-placeholder uma-screenshot-placeholder--error">
        {caption} unavailable
      </div>
    );
  }
  return (
    <figure className="uma-figure">
      <button
        type="button"
        className="uma-figure-btn"
        onClick={() => setZoomed(true)}
        aria-label={`Enlarge ${caption}`}
        data-testid="BTN_uma_screenshot"
      >
        <img className="uma-step-screenshot" src={src} alt={caption} />
      </button>
      <figcaption className="uma-figure-caption">{caption} — click to enlarge</figcaption>

      {zoomed && (
        <div
          className="uma-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={caption}
          onClick={() => setZoomed(false)}
          data-testid="MODAL_uma_lightbox"
        >
          <img className="uma-lightbox-img" src={src} alt={caption} />
          <span className="uma-lightbox-caption">{caption}</span>
        </div>
      )}
    </figure>
  );
};

/**
 * Numbered steps. Numbering + screenshot association come exclusively from
 * the structured ManualStep list (server post-validated against the retrieved
 * chunk set) — never parsed out of model free-text. Each step's screenshot is
 * rendered inline directly beneath that step — its appropriate place.
 */
const StepList: React.FC<{ steps: ManualStep[] }> = ({ steps }) => (
  <ol className="uma-step-list" data-testid="LST_uma_steps">
    {steps.map((step) => (
      <li key={step.number} className="uma-step-item">
        <span className="uma-step-number">{step.number}</span>
        <div className="uma-step-body">
          <p className="uma-step-instruction">{stripMarkdownImages(step.instruction)}</p>
          {step.screenshot && <ManualScreenshot url={step.screenshot} />}
        </div>
      </li>
    ))}
  </ol>
);

const CitationRow: React.FC<{ citations: Citation[] }> = ({ citations }) => {
  if (!citations.length) return null;
  return (
    <div className="uma-citation-row" data-testid="LST_uma_citations">
      {citations.map((c) => (
        <span key={c.chunk_id} className="uma-citation-badge">
          {c.chapter} — {c.section}
        </span>
      ))}
    </div>
  );
};

/**
 * Answer-level screenshots[] that are NOT already tied to a specific step.
 * These are the overview/context screens for the whole answer, so they render
 * right after the summary (before the steps) rather than dumped at the bottom.
 */
const AnswerScreenshots: React.FC<{ answer: ManualAnswer }> = ({ answer }) => {
  const inlineUrls = new Set(
    answer.steps.map((s) => s.screenshot).filter((s): s is string => !!s)
  );
  const extra = answer.screenshots.filter((s) => !inlineUrls.has(s));
  if (!extra.length) return null;
  return (
    <div className="uma-answer-screenshots" data-testid="LST_uma_answer_screenshots">
      {extra.map((url) => (
        <ManualScreenshot key={url} url={url} />
      ))}
    </div>
  );
};

/**
 * Thumbs up / down rating for one answer. A 'bad' rating (and 'good' too, for
 * analytics) is persisted server-side with the question + answer, for the
 * team's retrospective self-learning review. Fire-and-forget: a storage
 * failure just leaves the control re-enabled; it never blocks the chat.
 */
const FeedbackBar: React.FC<{
  answer: ManualAnswer;
  question: string;
  sessionId?: string;
}> = ({ answer, question, sessionId }) => {
  const [rated, setRated] = useState<FeedbackRating | null>(null);
  const [busy, setBusy] = useState(false);

  const rate = async (rating: FeedbackRating) => {
    if (busy || rated) return;
    setBusy(true);
    try {
      await submitFeedback({ rating, question, answer, session_id: sessionId });
      setRated(rating);
    } catch {
      // Non-fatal — allow a retry by leaving `rated` null.
    } finally {
      setBusy(false);
    }
  };

  if (rated) {
    return (
      <div className="uma-feedback uma-feedback--done" data-testid="TXT_uma_feedback_done">
        {rated === 'good' ? 'Thanks for the feedback!' : "Thanks — we'll use this to improve."}
      </div>
    );
  }

  return (
    <div className="uma-feedback" data-testid="ROW_uma_feedback">
      <span className="uma-feedback-label">Was this helpful?</span>
      <button
        type="button"
        className="uma-feedback-btn"
        onClick={() => rate('good')}
        disabled={busy}
        aria-label="Helpful"
        data-testid="BTN_uma_feedback_good"
      >
        <ThumbsUp size={14} />
      </button>
      <button
        type="button"
        className="uma-feedback-btn uma-feedback-btn--bad"
        onClick={() => rate('bad')}
        disabled={busy}
        aria-label="Not helpful"
        data-testid="BTN_uma_feedback_bad"
      >
        <ThumbsDown size={14} />
      </button>
    </div>
  );
};

export interface UmaMessageProps {
  answer: ManualAnswer;
  question?: string;
  sessionId?: string;
}

/** Renders one ManualAnswer, distinctly per answer_type. */
const UmaMessage: React.FC<UmaMessageProps> = ({ answer, question, sessionId }) => {
  const summary = stripMarkdownImages(answer.summary);

  if (answer.answer_type === 'refusal') {
    return (
      <div className="uma-message uma-message--refusal" data-testid="MSG_uma_refusal">
        <div className="uma-message-icon">
          <HelpCircle size={16} />
        </div>
        <p className="uma-message-text">{summary}</p>
      </div>
    );
  }

  if (answer.answer_type === 'clarification') {
    return (
      <div className="uma-message uma-message--clarification" data-testid="MSG_uma_clarification">
        <div className="uma-message-icon">
          <MessageCircleQuestion size={16} />
        </div>
        <p className="uma-message-text">{summary}</p>
      </div>
    );
  }

  // 'steps' | 'partial' — placement, top to bottom: summary → overview
  // screenshot(s) for context → numbered steps (each with its own inline
  // screenshot) → gaps → citations → feedback. The frontend concatenates the
  // pics into the flow at their appropriate places from the structured answer.
  return (
    <div className="uma-message uma-message--answer" data-testid="MSG_uma_answer">
      <p className="uma-message-summary">{summary}</p>
      <AnswerScreenshots answer={answer} />
      {answer.steps.length > 0 && <StepList steps={answer.steps} />}
      {answer.answer_type === 'partial' && answer.gaps && (
        <div className="uma-gaps-note" data-testid="TXT_uma_gaps">
          <AlertTriangle size={14} />
          <span>{stripMarkdownImages(answer.gaps)}</span>
        </div>
      )}
      <CitationRow citations={answer.citations} />
      {question && <FeedbackBar answer={answer} question={question} sessionId={sessionId} />}
    </div>
  );
};

export default UmaMessage;
